import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import { daDK } from '@mui/x-data-grid/locales';
import * as React from 'react';

import { IconButton } from '@mui/material';
import {
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
} from '@mui/x-data-grid';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { DateTime } from 'luxon';

export default function GoSharesDatagrid(props) {
  const { initialRows = [], handleSaveChanges } = props;

  const [rows, setRows] = React.useState(
    initialRows.map((row, index) => {
      let tempRow = { ...row };
      if (row.value === undefined) {
        if (row.percentage !== undefined) {
          tempRow.value = tempRow.percentage;
        }
      }
      return {
        ...row,
        value: tempRow.value,
        percentage: tempRow.value,
        id: index,
        isNew: false,
      };
    }),
  );
  const [rowModesModel, setRowModesModel] = React.useState({});

  const handleClick = () => {
    const id = DateTime.now().toMillis();
    setRows((oldRows) => [
      ...oldRows,
      {
        id,
        timestamp: DateTime.now()
          .set({ second: 0, millisecond: 0, minute: 0, hour: 0 })
          .toISO(),
        percentage: 0,
        isNew: true,
      },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'timestamp' },
    }));
  };
  const handleRowEditStop = (params, event) => {
    if (params?.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    } else {
      handleSaveClick(params.id)();
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setRows(rows.filter((row) => row.id !== id));
    handleSaveChanges(
      rows
        .filter((row) => row.id !== id)
        .filter((row) => !row.isNew)
        .map((row) => {
          return {
            timestamp: row.timestamp,
            value: row.percentage,
          };
        }),
    );
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow) => {
    newRow.timestamp = DateTime.fromJSDate(newRow.timestamp)
      .toLocal()
      .toUTC()
      .toISO();
    newRow.percentage = Math.min(1, Math.max(0, newRow.percentage / 100));
    const updatedRow = { ...newRow, isNew: false };
    const allRows = rows.map((row) =>
      row.id === newRow.id ? updatedRow : row,
    );
    setRows(allRows);
    handleSaveChanges(
      allRows
        .filter((row) => !row.isNew)
        .map((row) => {
          return {
            timestamp: row.timestamp,
            value: row.percentage,
          };
        }),
    );
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    {
      field: 'timestamp',
      headerName: 'Gyldig fra',
      type: 'date',
      width: 150,
      editable: true,
      valueGetter: (value) => {
        return DateTime.fromISO(value).toLocal().toJSDate();
      },
    },
    {
      field: 'percentage',
      headerName: 'GO Andel %',
      type: 'number',
      width: 100,
      align: 'left',
      headerAlign: 'left',
      editable: true,
      valueFormatter: (value) => {
        if (!value) return '0%';
        return value ? `${value}%` : '0%';
      },
      valueGetter: (value) => {
        return value ? value * 100 : 0;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 100,
      cellClassName: 'actions',
      renderHeader: () => {
        return (
          <IconButton onClick={handleClick}>
            <AddIcon color="primary" />
          </IconButton>
        );
      },
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={1}
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={2}
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={3}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key={4}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box
      sx={{
        height: 500,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      <DataGridPremium
        localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        hideFooter={true}
        sx={{
          '& ::-webkit-calendar-picker-indicator': {
            filter: (theme) => {
              // Check to see if Media-Queries are supported
              if (window.matchMedia) {
                // Check if the dark-mode Media-Query matches
                if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                  return theme.palette.mode === 'light'
                    ? 'invert(1)'
                    : 'invert(0)';
                } else {
                  return theme.palette.mode === 'light'
                    ? 'invert(0)'
                    : 'invert(1)';
                }
              } else {
                ('invert(1)');
              }
            },
          },
          // border: 'none',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            borderTop: '1px solid', // Optional: Adjust border color
            borderColor: 'divider',
          },
          '& .MuiTablePagination-root': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            borderTop: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDatagrid-root': {
            border: 0,
          },
          '.MuiDataGrid-withBorderColor': {
            border: 'none !important',
          },
          '& .MuiDatagrid-footerContainer': {
            border: 0,
          },
          '& .MuiDatagrid-bottomContainer': {
            border: 0,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            // borderRadius: 2,
            // border: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .MuiDataGrid-columnsContainer': {
            backgroundColor: 'blue',
          },
          '& .MuiDataGrid-columnHeaders': {
            // backgroundColor: 'blue', // Background color for column headers
            // color: 'white', // Text color
            // fontSize: '1rem', // Adjust font size if needed
            borderBottom: '1px solid', // Optional: Add a border
            borderColor: 'divider',
          },
          '& .selected-row': {
            backgroundColor: (theme) => theme.palette.action.selected,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          },
          '& .MuiDataGrid-scrollbar': {
            overflow: 'hidden !important',
          },
        }}
      />
    </Box>
  );
}
